/**
 * MBA Lab — pure ranking for a school type-ahead over the
 * /tools/data/schools-v1.json dataset.
 *
 * Deliberately its own file rather than living inside program-shortlist.js:
 * the ROI calculator's school picker is server-side today, and when it gains a
 * client-side one it should use exactly this ranking rather than growing a
 * second, subtly different implementation. Nothing here touches the DOM,
 * fetch, storage, or analytics.
 *
 * NO FUZZY MATCHING. Not edit distance, not trigrams, not "did you mean". A
 * near miss on a school name is the one place a helpful guess is actively
 * harmful - offering "Northwestern" to someone who typed "Northeastern"
 * invites picking the wrong school and then comparing its tuition. Every tier
 * below is an exact substring or word-prefix relationship the user can see for
 * themselves in the result they clicked.
 *
 * Normalization mirrors wwwroot/js/college-search.js's `norm()` so "St. John's"
 * and "st johns" meet in the middle the same way they do in the directory
 * type-ahead - two search boxes over the same schools should not disagree
 * about what a query means.
 */

/** Never more than this many results, however many match. */
export const RESULT_LIMIT = 8;

/** Below this many characters the caller should show nothing at all - one
 * letter matches most of the directory and ranks nothing usefully. */
export const MIN_QUERY_LENGTH = 2;

const RANK_EXACT_NAME = 0;
const RANK_NAME_PREFIX = 1;
const RANK_NAME_WORD_PREFIX = 2;
const RANK_NAME_SUBSTRING = 3;
const RANK_LOCATION = 4;
const RANK_NONE = -1;

// Combining diacritical marks, U+0300-U+036F. Built from code points rather
// than written as a literal character range so this file stays plain ASCII -
// a literal range of combining marks is invisible in a diff and trivially
// mangled by any tool that touches the file.
const DIACRITICS = new RegExp(`[${String.fromCharCode(0x0300)}-${String.fromCharCode(0x036f)}]`, 'g');

// Apostrophes (straight and typographic) and periods are dropped rather than
// turned into spaces, so "St. John's" normalizes to "st johns" and not
// "st john s".
const DROPPED_PUNCTUATION = new RegExp(`['${String.fromCharCode(0x2019)}.]`, 'g');

export function normalizeSearchText(value) {
  let s = String(value == null ? '' : value).toLowerCase();
  if (s.normalize) s = s.normalize('NFD').replace(DIACRITICS, '');
  return s
    .replace(DROPPED_PUNCTUATION, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function words(normalized) {
  return normalized ? normalized.split(' ') : [];
}

/** Every query token must prefix at least one word in the list. Two tokens may
 * legitimately hit the same word; this only decides whether the query is
 * satisfied, not how well. */
function everyTokenPrefixesAWord(tokens, wordList) {
  return tokens.every((token) => wordList.some((word) => word.indexOf(token) === 0));
}

/**
 * Precomputes the normalized forms for one dataset school. Callers that search
 * repeatedly (a type-ahead runs on every keystroke) should call this once per
 * school after the fetch resolves and hand the result to searchSchools.
 */
export function indexSchoolsForSearch(schools) {
  const list = Array.isArray(schools) ? schools : [];
  return list
    .filter((school) => school && typeof school.slug === 'string' && typeof school.name === 'string')
    .map((school) => {
      const name = normalizeSearchText(school.name);
      const location = normalizeSearchText(`${school.city || ''} ${school.stateCode || ''}`);
      return {
        school,
        name,
        nameWords: words(name),
        location,
        locationWords: words(location),
      };
    });
}

function rankOf(entry, query, tokens) {
  if (entry.name === query) return RANK_EXACT_NAME;
  if (entry.name.indexOf(query) === 0) return RANK_NAME_PREFIX;
  if (everyTokenPrefixesAWord(tokens, entry.nameWords)) return RANK_NAME_WORD_PREFIX;
  if (entry.name.indexOf(query) !== -1) return RANK_NAME_SUBSTRING;
  if (entry.location.indexOf(query) !== -1 || everyTokenPrefixesAWord(tokens, entry.locationWords)) {
    return RANK_LOCATION;
  }
  return RANK_NONE;
}

/**
 * @param {Array} indexed Output of indexSchoolsForSearch(), or a raw school
 *   array (indexed on the fly for one-off calls and tests).
 * @param {string} rawQuery
 * @param {{limit?: number}} [options]
 * @returns {Array} the matching dataset school objects, best first, capped.
 *
 * Ordering: exact name, then name prefix, then every-token-prefixes-a-name-word,
 * then name substring, then city/state - so a location match can never outrank
 * a name match. Ties break on normalized name, then slug, so the same query
 * over the same data always produces the same list in the same order (a
 * type-ahead whose rows reshuffle between keystrokes is unusable, and a
 * non-deterministic order would make any result_rank analytics meaningless).
 */
export function searchSchools(indexed, rawQuery, { limit = RESULT_LIMIT } = {}) {
  const query = normalizeSearchText(rawQuery);
  if (query.length < MIN_QUERY_LENGTH) return [];

  const entries =
    Array.isArray(indexed) && indexed.length && indexed[0] && indexed[0].school
      ? indexed
      : indexSchoolsForSearch(indexed);

  const tokens = words(query);
  const matched = [];
  for (const entry of entries) {
    const rank = rankOf(entry, query, tokens);
    if (rank !== RANK_NONE) matched.push({ entry, rank });
  }

  matched.sort((a, b) => {
    if (a.rank !== b.rank) return a.rank - b.rank;
    if (a.entry.name !== b.entry.name) return a.entry.name < b.entry.name ? -1 : 1;
    if (a.entry.school.slug === b.entry.school.slug) return 0;
    return a.entry.school.slug < b.entry.school.slug ? -1 : 1;
  });

  return matched.slice(0, Math.max(0, limit)).map((m) => m.entry.school);
}
